import { PATCH_NOTES_OMIT_FIELDS } from "./patch-notes-omit.config";

/**
 * Normalize a field path by converting array bracket notation to wildcard notation.
 * Example: "weapons[0].ammo.field" -> "weapons.*.ammo.field"
 */
function normalizePath(path: string): string {
  // Replace array bracket notation [0], [1], etc. with wildcard
  return path.replace(/\[\d+\]/g, ".*");
}

/**
 * Check if a field path should be omitted from patch notes display.
 * 
 * @param path - The field path to check (e.g., "weapons[0].ammo.staticAccuracyOverDistance")
 * @param omitFields - Optional array of field paths to omit. Defaults to PATCH_NOTES_OMIT_FIELDS.
 * @returns true if the field should be omitted, false otherwise
 * 
 * @example
 * shouldOmitField("weapons[0].ammo.staticAccuracyOverDistance") // true
 * shouldOmitField("weapons[0].ammo.heDamage") // false
 * shouldOmitField("name") // false
 */
export function shouldOmitField(
  path: string,
  omitFields: readonly string[] = PATCH_NOTES_OMIT_FIELDS
): boolean {
  if (!path || omitFields.length === 0) {
    return false;
  }

  // Normalize the path to handle array indices
  const normalizedPath = normalizePath(path);

  // Check each omit pattern
  for (const omitPattern of omitFields) {
    // Normalize the pattern as well
    const normalizedPattern = normalizePath(omitPattern);

    // Exact match
    if (normalizedPath === normalizedPattern) {
      return true;
    }

    // Check if the path ends with the pattern (for nested matches)
    // e.g., "weapons.*.ammo.staticAccuracyOverDistance" matches "weapons[0].ammo.staticAccuracyOverDistance"
    if (normalizedPath.endsWith(normalizedPattern)) {
      // Check if it's a complete segment match (not just a substring)
      const beforePattern = normalizedPath.slice(0, -normalizedPattern.length);
      if (beforePattern === "" || beforePattern.endsWith(".")) {
        return true;
      }
    }

    // Check if the pattern matches as a suffix with proper boundary
    // e.g., "ammo.staticAccuracyOverDistance" matches "weapons[0].ammo.staticAccuracyOverDistance"
    if (normalizedPattern.startsWith(".")) {
      const patternWithoutDot = normalizedPattern.slice(1);
      if (normalizedPath.endsWith("." + patternWithoutDot)) {
        return true;
      }
    } else {
      // Pattern without leading dot - check if path contains it as a complete segment
      const segments = normalizedPath.split(".");
      const patternSegments = normalizedPattern.split(".");
      
      // Check if pattern segments match the end of path segments
      if (segments.length >= patternSegments.length) {
        const pathEnd = segments.slice(-patternSegments.length);
        if (pathEnd.join(".") === patternSegments.join(".")) {
          return true;
        }
      }
    }

    // Wildcard matching: if pattern has wildcards, try to match
    if (normalizedPattern.includes("*")) {
      // Convert pattern to regex
      const regexPattern = normalizedPattern
        .replace(/\./g, "\\.")
        .replace(/\*/g, "[^.]+");
      const regex = new RegExp(`^${regexPattern}$`);
      if (regex.test(normalizedPath)) {
        return true;
      }
    }
  }

  return false;
}

